import Editor from "../editor";
import ProjectConfig from "./project-config";
import { engineTypeFiles } from "../../engine-files";
import { Builder } from "./builder";
import Bundler from "./bundler";
import { RunningState, System, type UUID } from "@flint/runtime/system";
import Metadata from "@flint/shared/metadata";
import { ProjectLoader } from "@flint/runtime/project-loader";
import type { BrowserFileSystem } from "@flint/shared/file-system";
import { AbstractFileSystem as AbstractFileSystem, type FileSystemEntry } from "@flint/shared/file-system";
import { AssetRegistry } from "@flint/runtime/assets";
import { EditorLayer as EditorLayer } from "../editor-layer";

import type SlInput from "@shoelace-style/shoelace/dist/components/input/input.js";
import type SlButton from "@shoelace-style/shoelace/dist/components/button/button.js";
import { CodeEditor } from "../ui/code-editor";
import { CasingHandler } from "../casing-handler";
import { DependencyService } from "../services/dependency-service";
import { componentFileName, makeComponentSource } from "@flint/build";
import { activeWindowService, editorAssetStore, editorSelectionService } from "../ui/window-services";
import { refreshEditorWindows } from "../layout";
import { ExternalChangeDialog } from "../external-change-dialog";

type ProjectArchiveEntry = {
    kind: "directory";
    path: string;
} | {
    kind: "file";
    path: string;
    data: string;
};

type ProjectArchive = {
    format: "flint-project-archive";
    version: 1;
    createdAt: string;
    entries: ProjectArchiveEntry[];
};
// export class FileTracker {
//     private constructor() { }

//     static timestamps = new Map<string, number>();
//     static intervalId: number | null = null;

//     private static debounceTimer: number | null = null;
//     private static debounceDelay = 200; // auto adjustable

//     static async startWatchingDirectory(
//         dirHandle: FileSystemDirectoryHandle
//     ) {
//         if (this.intervalId !== null) return;

//         this.intervalId = setInterval(async () => {
//             const updated = await this.directoryWasUpdated(dirHandle);
//             if (updated) {
//                 this.scheduleRebuild();
//             }
//         }, this.debounceDelay);
//     }

//     static stopWatching() {
//         if (this.intervalId !== null) {
//             clearInterval(this.intervalId);
//             this.intervalId = null;
//         }

//         if (this.debounceTimer !== null) {
//             clearTimeout(this.debounceTimer);
//             this.debounceTimer = null;
//         }
//     }

//     private static scheduleRebuild() {
//         /* If timer already started -> stop it and start a new one */
//         if (this.debounceTimer !== null) {
//             clearTimeout(this.debounceTimer);
//         }

//         this.debounceTimer = setTimeout(async () => {
//             const start = performance.now();
//             await Builder.buildForEditor(false);
//             const end = performance.now();

//             FileTracker.debounceDelay = end - start;
//             this.stopWatching();
//             this.startWatchingDirectory(Project.folderHandle);

//             this.debounceTimer = null;
//         }, this.debounceDelay);
//     }

//     private static async directoryWasUpdated(
//         dirHandle: FileSystemDirectoryHandle,
//         currentPath = ""
//     ) {
//         let anyUpdated = false;

//         for await (const entry of dirHandle.values()) {
//             const fullPath = currentPath ? `${currentPath}/${entry.name}` : entry.name;

//             if (entry.kind === "file") {
//                 if (fullPath.endsWith("metadata.json")) continue; // dont react on metadata updates

//                 const fileUpdated = await this.wasUpdated(fullPath, entry);
//                 if (fileUpdated) anyUpdated = true;
//             }

//             if (entry.kind === "directory") {
//                 const subUpdated = await this.directoryWasUpdated(entry, fullPath);
//                 if (subUpdated) anyUpdated = true;
//             }
//         }

//         return anyUpdated;
//     }

//     private static async wasUpdated(path: string, fileHandle: FileSystemFileHandle) {
//         const file = await fileHandle.getFile();
//         const newTimestamp = file.lastModified;
//         const prevTimestamp = this.timestamps.get(path);

//         this.timestamps.set(path, newTimestamp);

//         return prevTimestamp !== undefined && prevTimestamp !== newTimestamp;
//     }
// }


export class Project {
    private static createComponentDialog: HTMLElement & { show: () => void; hide: () => void };
    private static createComponentButton: SlButton;
    private static createComponentInput: SlInput;

    public static names: Map<UUID, string> = new Map();

    public static get folderHandle(): FileSystemDirectoryHandle | undefined {
        return (System.fileSystem as BrowserFileSystem).getRootHandle();
    }

    private constructor() { }

    private static addEditorLayer(): void {
        const layer = new EditorLayer();
        System.world.unshiftLayer(layer);
        layer.attach();
        if (System.runningState === RunningState.Running) {
            layer.start();
        }
    }

    static {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        Project.createComponentDialog = document.getElementById("create-component-dialog")! as any;

        Project.createComponentButton = Project.createComponentDialog.querySelector("sl-button") as SlButton;
        Project.createComponentButton.addEventListener("click", () => {
            Project.createComponentDialog.hide();
            Project.createComponent(Project.createComponentInput.value.trim());
        });

        Project.createComponentInput = Project.createComponentDialog.querySelector("sl-input") as SlInput;
        Project.createComponentInput.addEventListener("sl-input", () => {
            Project.createComponentButton.disabled = Project.createComponentInput.value.trim() === "";
        });
    }

    public static async run() {
        const success = await Builder.buildForEditor();

        if (success) {
            await Project.saveProject(); // NOTE: this takes up to ~150ms
        }

        return success;
    }

    public static async stop() {
        const success = await Project.loadProject();
        refreshEditorWindows("Hierarchy");
        refreshEditorWindows("Inspector");
        editorSelectionService.setSelectedId(editorSelectionService.getSelectedId());
        return success;
    }

    public static async openProject(folderHandle: FileSystemDirectoryHandle) {
        await Project.startupProject(folderHandle);
        await Project.loadProject();
        Project.markAsSaved();
        refreshEditorWindows("Hierarchy");

        // setInterval(async () => {
        //     await Project.saveProject();
        // }, 60000); // FIXME: implement autosave in a better way
    }

    public static async newProject(folderHandle: FileSystemDirectoryHandle) {
        if (await Project.startupProject(folderHandle) || !await Project.loadProject()) {
            await Project.initializeDefaultProject();
            refreshEditorWindows("Hierarchy");
        }

        await Project.saveProject();
        Project.markAsSaved();
        if (!System.world.getLayers().some(layer => layer instanceof EditorLayer)) {
            Project.addEditorLayer();
        }
        refreshEditorWindows("Hierarchy");

        // setInterval(async () => {
        //     await Project.saveProject();
        // }, 60000); // FIXME: implement autosave in a better way
    }

    public static async buildAndRun() {
        return !!(+await Builder.preview() & +await Builder.build());
    }

    public static async exportProjectArchive(): Promise<Uint8Array> {
        if (!System.fileSystem.started) {
            throw new Error("Open project first.");
        }

        const archive: ProjectArchive = {
            format: "flint-project-archive",
            version: 1,
            createdAt: new Date().toISOString(),
            entries: await Project.collectArchiveEntries()
        };

        const archiveBlob = new Blob([JSON.stringify(archive)], { type: "application/json" });
        const compressedStream = archiveBlob.stream().pipeThrough(new CompressionStream("gzip"));
        const compressedData = await new Response(compressedStream).arrayBuffer();

        return new Uint8Array(compressedData);
    }

    public static async importProjectArchive(compressedData: Uint8Array): Promise<void> {
        if (!System.fileSystem.started) {
            throw new Error("Open project first.");
        }

        const compressedBuffer = AbstractFileSystem.toArrayBuffer(compressedData);
        const decompressedStream = new Blob([compressedBuffer]).stream().pipeThrough(new DecompressionStream("gzip"));
        const decodedJson = new TextDecoder().decode(await new Response(decompressedStream).arrayBuffer());

        const parsed = JSON.parse(decodedJson) as Partial<ProjectArchive>;
        if (parsed.format !== "flint-project-archive" || parsed.version !== 1 || !Array.isArray(parsed.entries)) {
            throw new Error("Invalid archive format.");
        }

        await Project.clearDirectoryRecursive("");

        const directories = parsed.entries
            .filter((entry): entry is Extract<ProjectArchiveEntry, { kind: "directory"; }> => entry.kind === "directory")
            .sort((a, b) => a.path.split("/").length - b.path.split("/").length);
        for (const directory of directories) {
            await System.fileSystem.createDir(directory.path);
        }

        const files = parsed.entries
            .filter((entry): entry is Extract<ProjectArchiveEntry, { kind: "file"; }> => entry.kind === "file");
        for (const file of files) {
            await System.fileSystem.writeFile(file.path, Project.base64ToBytes(file.data));
        }

        await ProjectConfig.ensureLoaded();
        await Builder.buildForEditor(true, { copyAssets: true });
        await Project.loadProject();
        refreshEditorWindows("Hierarchy");
    }

    public static async saveProject(): Promise<boolean> {
        if (await Project.diskChangedExternally()) {
            const choice = await ExternalChangeDialog.show();
            if (choice === "cancel") {
                return false;
            }
        }

        const data = ProjectLoader.serialize({ layers: System.world.getLayers().filter(l => !(l instanceof EditorLayer)), assets: AssetRegistry.serialize() });
        const writeProject = System.fileSystem.writeTextFile("project.json", data);

        const writeMetadata = Metadata.saveToFile(System.world.getLayers().filter(l => !(l instanceof EditorLayer)));

        await Promise.all([writeProject, writeMetadata]);

        Project.lastSavedDiskContent = data;
        Project.markAsSaved();
        return true;
    }

    /**
     * Exact `project.json` text the editor last wrote (or loaded).
     * Used to detect changes made outside the editor, which a blind
     * save would otherwise silently overwrite. `null` means unknown
     * yet (e.g. before the first load/save) - no warning in that case.
     */
    private static lastSavedDiskContent: string | null = null;

    /**
     * `true` when `project.json` on disk no longer matches what the
     * editor last wrote or loaded. Never blocks saving on FS errors.
     */
    private static async diskChangedExternally(): Promise<boolean> {
        if (Project.lastSavedDiskContent === null) {
            return false;
        }
        if (!System.fileSystem.started) {
            return false;
        }
        try {
            if (!await System.fileSystem.fileExists("project.json")) {
                return false;
            }
            const current = await System.fileSystem.readTextFile("project.json");
            return current !== Project.lastSavedDiskContent;
        } catch {
            return false;
        }
    }

    private static lastSavedHash: string = "";

    public static markAsSaved() {
        Project.lastSavedHash = Project.computeStateHash();
    }

    public static computeStateHash(): string {
        return ProjectLoader.serialize({ layers: System.world.getLayers().filter(l => !(l instanceof EditorLayer)), assets: AssetRegistry.serialize() });
    }

    public static needsSave(): boolean {
        if (!Project.lastSavedHash) {
            return false;
        }
        return Project.computeStateHash() !== Project.lastSavedHash;
    }

    private static async loadProject() {
        try {
            const json = await System.fileSystem.readTextFile("project.json");

            const projectData = ProjectLoader.deserialize(JSON.parse(json));

            const editorLayerIndex = System.world.getLayers().findIndex(l => l instanceof EditorLayer);
            const editorLayer = System.world.getLayers()[editorLayerIndex]!;
            System.world.removeLayer(editorLayer, false);

            await ProjectLoader.load(projectData);

            if (editorLayer) {
                System.world.unshiftLayer(editorLayer);
            }
            else {
                Project.addEditorLayer();
            }

            await Metadata.loadFromFile();

            Project.lastSavedDiskContent = json;
            return true;
        } catch (e) {
            console.log("could not load the project:", e);
            return false;
        }
    }

    public static loadFlintTypes() {
        return Project.copyTypesToProject(window.location.href.replace(/index\.html$/, "") + "/types/", (total, loaded) => {
            Editor.loadingDialogProgressBar.value = (loaded / total) * 100;
        });
    }

    private static async startupProject(handle: FileSystemDirectoryHandle): Promise<boolean> {
        await Bundler.disposeAll();
        System.fileSystem.setRootHandle(handle);
        const wasCreated = await ProjectConfig.ensureLoaded();

        Editor.syncSettingsFromProjectConfig();

        try {
            const { TerminalProvider } = await import("../ui/terminal-provider");
            await TerminalProvider.notifyProjectOpened();
        } catch { /* ignore */ }
        try {
            await DependencyService.ensureInstalled();
        } catch { /* ignore */ }

        await Project.getAllTextFiles();

        if (!await System.fileSystem.dirExists("flint")) {
            Editor.loadingDialogProgressBar.value = 0;
            Editor.loadingDialogProgressBar.indeterminate = false;
            Editor.loadingDialog.show();
            await Project.loadFlintTypes();
        }
        await System.fileSystem.createDir("assets");

        await Builder.buildForEditor(true, { copyAssets: true });

        try {
            await CodeEditor.resetAndReloadLibraries();
        } catch { /* ignore */ }

        Editor.loadingDialog.hide();

        // await FileTracker.startWatchingDirectory();
        return wasCreated;
    }

    public static async getAllTextFiles(path = "") {
        const files: { path: string }[] = [];
        const assets: { id: string, name: string, type: string, path: string }[] = [];

        async function traverse(currentPath: string) {
            let entries: FileSystemEntry[] = [];
            try {
                entries = await System.fileSystem.listDirEntries(currentPath);
            } catch {
                // If FS doesn't support listDir, just returning
                return;
            }

            for (const { name, kind } of entries) {
                if (name === "flint" || name === ".git" || name === "vendor" || name === "build") continue;

                const fullPath = currentPath ? `${currentPath}/${name}` : name;

                if (kind === "directory") {
                    // Adding folder
                    assets.push({
                        id: crypto.randomUUID(),
                        name,
                        type: "folder",
                        path: "/" + fullPath
                    });
                    await traverse(fullPath);
                } else if (name.endsWith(".ts") || name.endsWith(".json") || (fullPath.startsWith("node_modules/") && (name.endsWith(".js") || name.endsWith(".mjs") || name.endsWith(".cjs")))) {
                    // Adding file (node_modules js files are needed for npm package bundling)
                    // console.log(fullPath);
                    const isNodeModule = fullPath.startsWith("node_modules/");
                    assets.push({
                        id: crypto.randomUUID(),
                        name,
                        type: name.endsWith(".ts") ? "component" : isNodeModule ? "file" : "json",
                        path: "/" + fullPath
                    });
                    files.push({ path: fullPath });
                }
            }
        }

        await traverse(path);

        return { files, assets };
    }


    public static async openInFileEditor(pathToOpen: string) {
        if (ProjectConfig.config.rootPath === "virtual") {
            CodeEditor.openFile(pathToOpen);
            return;
        }
        if (!ProjectConfig.config.rootPath) {
            const path = prompt("Due to browser restrictions - to open file in VSCode enter absolute path to your project folder\n(for example: C:/path/to/your/project/folder):", "");
            if (!path) {
                return;
            }
            ProjectConfig.config.rootPath = path;
            await ProjectConfig.save();
        }
        window.location.href = "vscode://file/" + ProjectConfig.config.rootPath + pathToOpen;
    }

    public static showCreateComponentWindow() {
        Project.createComponentButton.disabled = true;
        Project.createComponentInput.value = "";
        Project.createComponentDialog.show();
    }

    public static async createComponent(name: string) {
        name = CasingHandler.joinToPascalCase(name);
        const fileBaseName = CasingHandler.splitPascalCase(name, "-");

        const assetPath = activeWindowService.getPreferredAssetsPath().replace(/^\//, "");
        const relativeFilePath = `${assetPath}/${componentFileName(name)}`;

        const fileContent = makeComponentSource(name);

        const parts = assetPath.split("/").filter(Boolean);
        let currentPath = "";
        for (const part of parts) {
            currentPath = currentPath ? `${currentPath}/${part}` : part;
            const exists = await System.fileSystem.fileExists(currentPath);
            if (!exists) {
                await System.fileSystem.createDir(currentPath);
            }
        }

        await System.fileSystem.writeTextFile(relativeFilePath, fileContent);

        editorAssetStore.add({
            id: crypto.randomUUID(),
            name: fileBaseName + ".ts",
            type: "component",
            path: "/" + relativeFilePath,
            data: name
        });

        ProjectConfig.config.components.push({ name, file: "/" + relativeFilePath });
        await ProjectConfig.save();

        await Project.openInFileEditor("/" + relativeFilePath);
    }

    private static async initializeDefaultProject() {
        const template = Editor.defaultProject;
        await Project.ensureTemplateFiles(template.files);
        await Project.ensureTemplateComponents(template.components);
        await Builder.buildForEditor(true, { copyAssets: true });
        await ProjectLoader.load(Editor.defaultProject.data);
    }

    private static async ensureTemplateFiles(files: { path: string, content: string }[]) {
        for (const file of files) {
            const fileExists = await System.fileSystem.fileExists(file.path);
            if (!fileExists) {
                await System.fileSystem.writeTextFile(file.path, file.content);
            }
        }
    }

    private static async ensureTemplateComponents(components: { name: string, file: string }[]) {
        let updated = false;

        for (const component of components) {
            if (ProjectConfig.config.components.some(existing => existing.name === component.name)) {
                continue;
            }

            ProjectConfig.config.components.push(component);
            updated = true;
        }

        if (updated) {
            await ProjectConfig.save();
        }
    }

    public static async deleteComponent(name: string) {
        const fileBaseName = CasingHandler.splitPascalCase(name, "-");
        const assetPath = activeWindowService.getPreferredAssetsPath().replace(/^\//, "");
        const relativeFilePath = `${assetPath}/${fileBaseName}.ts`;

        try {
            const exists = await System.fileSystem.fileExists(relativeFilePath);
            if (exists) {
                await System.fileSystem.delete(relativeFilePath);
            } else {
                console.warn("File does not exist:", relativeFilePath);
            }
        } catch (e) {
            console.error("Failed to delete file:", e);
        }

        // Deleting component from config
        const index = ProjectConfig.config.components.findIndex(c => c.name === name);
        if (index !== -1) {
            ProjectConfig.config.components.splice(index, 1);
            await ProjectConfig.save();
        }

        // Updating UI
        editorAssetStore.remove("/" + relativeFilePath);
    }


    private static async copyTypesToProject(
        _typesBaseUrl: string,
        callback?: (total: number, loaded: number) => void
    ) {
        const allFiles = Object.keys(engineTypeFiles);

        let loaded = 0;

        const tasks = allFiles.map(async (filePath) => {
            const content = engineTypeFiles[filePath]!;

            const pathParts = filePath.split("/");
            const fileName = pathParts.pop()!;
            const dirPath = [...pathParts].filter(Boolean).join("/");

            if (dirPath) {
                await System.fileSystem.createDir(dirPath); // create intermediate directories
            }

            await System.fileSystem.writeTextFile(
                dirPath ? `${dirPath}/${fileName}` : fileName,
                content
            );

            if (callback) callback(allFiles.length, ++loaded);
        });

        await Promise.all(tasks);
        console.log("All type/json files copied!");
    }

    private static async collectArchiveEntries(path = "", entries: ProjectArchiveEntry[] = []) {
        const names = await System.fileSystem.listDir(path).catch(() => []);

        for (const name of names) {
            if (!path && (name === "build" || name === "flint")) {
                continue;
            }

            const fullPath = path ? `${path}/${name}` : name;
            const isDirectory = await Project.isDirectory(fullPath);

            if (isDirectory) {
                entries.push({
                    kind: "directory",
                    path: fullPath
                });
                await Project.collectArchiveEntries(fullPath, entries);
                continue;
            }

            entries.push({
                kind: "file",
                path: fullPath,
                data: Project.bytesToBase64(await System.fileSystem.readFile(fullPath))
            });
        }

        return entries;
    }

    private static async clearDirectoryRecursive(path = "") {
        const names = await System.fileSystem.listDir(path).catch(() => []);

        for (const name of names) {
            const fullPath = path ? `${path}/${name}` : name;
            const isDirectory = await Project.isDirectory(fullPath);

            if (isDirectory) {
                await Project.clearDirectoryRecursive(fullPath);
                await System.fileSystem.deleteDir(fullPath).catch(() => undefined);
            } else {
                await System.fileSystem.delete(fullPath).catch(() => undefined);
            }
        }
    }

    private static async isDirectory(path: string) {
        return System.fileSystem.listDir(path).then(() => true).catch(() => false);
    }

    private static bytesToBase64(bytes: Uint8Array) {
        let binary = "";
        const chunkSize = 0x8000;

        for (let i = 0; i < bytes.length; i += chunkSize) {
            const chunk = bytes.subarray(i, i + chunkSize);
            binary += String.fromCharCode(...chunk);
        }

        return btoa(binary);
    }

    private static base64ToBytes(base64: string) {
        const binary = atob(base64);
        const bytes = new Uint8Array(binary.length);

        for (let i = 0; i < binary.length; i++) {
            bytes[i] = binary.charCodeAt(i);
        }

        return bytes;
    }
}
